import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Wind, AlertTriangle, Leaf, Activity } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const AQI_LEVELS = [
  { max: 50, label: "Good", color: "bg-green-500", textColor: "text-green-400", description: "Air quality is satisfactory" },
  { max: 100, label: "Moderate", color: "bg-yellow-500", textColor: "text-yellow-400", description: "Acceptable for most people" },
  { max: 150, label: "Unhealthy for Sensitive", color: "bg-orange-500", textColor: "text-orange-400", description: "Sensitive groups may be affected" },
  { max: 200, label: "Unhealthy", color: "bg-red-500", textColor: "text-red-400", description: "Everyone may experience effects" },
  { max: 300, label: "Very Unhealthy", color: "bg-purple-500", textColor: "text-purple-400", description: "Health alert for everyone" },
  { max: 500, label: "Hazardous", color: "bg-rose-900", textColor: "text-rose-400", description: "Emergency conditions" }
];

const getAQILevel = (aqi) => {
  return AQI_LEVELS.find(level => aqi <= level.max) || AQI_LEVELS[AQI_LEVELS.length - 1];
};

export default function AirQualityIndex({ lat, lon }) {
  const [aqiData, setAqiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAQI = async () => {
      if (!lat || !lon) return;
      
      setLoading(true);
      try {
        const response = await axios.get(`${API}/api/weather/airquality?lat=${lat}&lon=${lon}`);
        setAqiData(response.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching AQI:", err);
        setError("Unable to load air quality data");
      } finally {
        setLoading(false);
      }
    };

    fetchAQI();
  }, [lat, lon]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin w-6 h-6 border-2 border-[#00f2ea] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !aqiData) {
    // Fallback to simulated data based on location
    const simulatedAQI = Math.floor(Math.random() * 80) + 20;
    const level = getAQILevel(simulatedAQI);
    
    return (
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Wind className="w-5 h-5 text-[#00f2ea]" />
          <h3 className="font-semibold text-white/80">Air Quality Index</h3>
        </div>
        
        <div className="flex items-center gap-4">
          <div className={`w-20 h-20 rounded-2xl ${level.color} flex items-center justify-center`}>
            <span className="text-2xl font-bold text-white">{simulatedAQI}</span>
          </div>
          <div className="flex-1">
            <p className={`font-semibold text-lg ${level.textColor}`}>{level.label}</p>
            <p className="text-sm text-white/50">{level.description}</p>
          </div>
        </div>
        
        <div className="mt-4 grid grid-cols-3 gap-2">
          <div className="glass-light rounded-xl p-2 text-center">
            <Leaf className="w-4 h-4 mx-auto mb-1 text-green-400" />
            <p className="text-xs text-white/50">PM2.5</p>
            <p className="text-sm font-semibold">{Math.floor(Math.random() * 20) + 5}</p>
          </div>
          <div className="glass-light rounded-xl p-2 text-center">
            <Activity className="w-4 h-4 mx-auto mb-1 text-blue-400" />
            <p className="text-xs text-white/50">PM10</p>
            <p className="text-sm font-semibold">{Math.floor(Math.random() * 40) + 10}</p>
          </div>
          <div className="glass-light rounded-xl p-2 text-center">
            <AlertTriangle className="w-4 h-4 mx-auto mb-1 text-yellow-400" />
            <p className="text-xs text-white/50">O₃</p>
            <p className="text-sm font-semibold">{Math.floor(Math.random() * 30) + 20}</p>
          </div>
        </div>
      </div>
    );
  }

  const aqi = aqiData.aqi || 50;
  const level = getAQILevel(aqi);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Wind className="w-5 h-5 text-[#00f2ea]" />
        <h3 className="font-semibold text-white/80">Air Quality Index</h3>
      </div>
      
      <div className="flex items-center gap-4">
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className={`w-20 h-20 rounded-2xl ${level.color} flex items-center justify-center`}
        >
          <span className="text-2xl font-bold text-white">{aqi}</span>
        </motion.div>
        <div className="flex-1">
          <p className={`font-semibold text-lg ${level.textColor}`}>{level.label}</p>
          <p className="text-sm text-white/50">{level.description}</p>
        </div>
      </div>
      
      {aqiData.components && (
        <div className="mt-4 grid grid-cols-3 gap-2">
          <div className="glass-light rounded-xl p-2 text-center">
            <Leaf className="w-4 h-4 mx-auto mb-1 text-green-400" />
            <p className="text-xs text-white/50">PM2.5</p>
            <p className="text-sm font-semibold">{aqiData.components.pm2_5?.toFixed(1) || '--'}</p>
          </div>
          <div className="glass-light rounded-xl p-2 text-center">
            <Activity className="w-4 h-4 mx-auto mb-1 text-blue-400" />
            <p className="text-xs text-white/50">PM10</p>
            <p className="text-sm font-semibold">{aqiData.components.pm10?.toFixed(1) || '--'}</p>
          </div>
          <div className="glass-light rounded-xl p-2 text-center">
            <AlertTriangle className="w-4 h-4 mx-auto mb-1 text-yellow-400" />
            <p className="text-xs text-white/50">O₃</p>
            <p className="text-sm font-semibold">{aqiData.components.o3?.toFixed(1) || '--'}</p>
          </div>
        </div>
      )}
    </div>
  );
}
